import {
  Box,
  Button,
  Flex,
  Icon,
  Image,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import type { CardProps } from '@ui/components/Card';
import { Card } from '@ui/components/Card';
import { pathDict } from '@ui/config/paths';
import useBoostMultiplier from '@ui/hooks/useBoostMultiplier';
import type { CoinMode } from '@ui/hooks/useCoinMode';
import { useNumberOfStakedNfts } from '@ui/hooks/useCruisers';
import useFarmAprs from '@ui/hooks/useFarmAprs';
import useGardenApy from '@ui/hooks/useGardenApy';
import useLpAmountsStaked from '@ui/hooks/useLpAmountsStaked';
import usePendingVno from '@ui/hooks/usePendingVno';
import { useVnoUsdValue } from '@ui/hooks/useVnoUsdValue';
import { useTranslations } from '@ui/i18n';
import Link from '@ui/overrides/next/link';
import { useRouter } from '@ui/overrides/next/router';
import { formatTokenAmount } from '@ui/utils/format';
import type { GardenType } from '@ui/utils/garden';
import {
  formatGardenApy,
  gardenTokenDict,
  getGardeAvatarUrl,
  getGardenColorThemeByCoinMode,
  getGardenName,
} from '@ui/utils/garden';
import { GardenBooster } from '@ui/views/Garden/GardenBooster';
import GardenNftOverview from '@ui/views/Garden/GardenNftOverview';
import { ArrowDown } from '@veno-app/icons';
import React from 'react';

import Info from '../Info';
import { LPTokenIcon } from '../LPTokenIcon';

export type GardenInfoCardProps = CardProps & {
  coinMode: CoinMode;
  gardenType: GardenType;
};

export const GardenInfoCard = ({
  gardenType,
  coinMode,
  ...props
}: GardenInfoCardProps) => {
  const t = useTranslations();
  const { staked, apy } = useGardenInfoCard(gardenType);
  const { data: boostMultiplier } = useBoostMultiplier(gardenType);
  const { pendingVno } = usePendingVno(gardenType);
  const { data: pendingVnoUsd, isLoading: pendingVnoUsdLoading } =
    useVnoUsdValue(pendingVno);
  const router = useRouter();
  const { data: numberOfStakedCruisers } = useNumberOfStakedNfts(gardenType);
  const { isBoosted } = useFarmAprs();
  const expandDisclosure = useDisclosure();
  const isHarvestHidden = !pendingVno || pendingVno.lte(0);

  return (
    <Card
      colorTheme={getGardenColorThemeByCoinMode(coinMode)}
      p="16px"
      m="0"
      isActive={expandDisclosure.isOpen}
      onClick={expandDisclosure.onToggle}
      {...props}
    >
      <Flex alignItems="center" gap="20px">
        <Flex
          justify="center"
          align="center"
          bg="white"
          rounded="full"
          position="relative"
          borderColor="brand-white"
          borderWidth="2px"
        >
          <Image
            w="60px"
            h="60px"
            src={getGardeAvatarUrl(gardenType)}
            alt="garden avatar"
          />
          {isBoosted && <GardenBooster position="absolute" top="0" />}
        </Flex>
        <Box flex="1">
          <Flex gap="8px" justify="space-between">
            <Link href={pathDict[coinMode].garden[gardenType].PLANT} passHref>
              <Button
                as="a"
                variant="link"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <Text color="text-active" textStyle="h4Bold">
                  {getGardenName(coinMode, gardenType, false)}
                </Text>
              </Button>
            </Link>
            <Icon
              as={ArrowDown}
              boxSize="16px"
              transform={expandDisclosure.isOpen ? 'rotate(180deg)' : undefined}
            />
          </Flex>
          <Flex gap="2px" mt="8px">
            <Text>{t('Planted')}:</Text>
            {staked ? (
              <LPTokenIcon
                coinMode={coinMode}
                gardenType={gardenType}
                textStyle="bodyBold"
              >
                {formatTokenAmount(
                  staked,
                  4,
                  gardenTokenDict[coinMode][gardenType].decimals,
                )}
              </LPTokenIcon>
            ) : (
              '-'
            )}
          </Flex>
        </Box>
      </Flex>
      <Flex
        hidden={!expandDisclosure.isOpen && isHarvestHidden}
        gap={4}
        flexDir="column"
        mt="20px"
      >
        <Info
          hidden={!expandDisclosure.isOpen}
          title="APR"
          value={
            apy !== undefined ? (
              <Box
                color={
                  (boostMultiplier ?? 0) > 1
                    ? 'garden.highlight'
                    : 'text.normal'
                }
              >
                {apy}
              </Box>
            ) : undefined
          }
        />
        <Info
          hidden={!expandDisclosure.isOpen}
          title={t('Helpers employed')}
          value={
            numberOfStakedCruisers !== undefined && (
              <>
                <Box>{numberOfStakedCruisers}</Box>
                <GardenNftOverview gardenType={gardenType} />
              </>
            )
          }
        />
        <Info
          hidden={!expandDisclosure.isOpen}
          title={t('Total active boost')}
          value={boostMultiplier ? boostMultiplier.toFixed(2) + 'x' : undefined}
        />
        <Info
          hidden={isHarvestHidden}
          title={t('Rewards')}
          value={
            pendingVno ? formatTokenAmount(pendingVno) + ' VNO' : undefined
          }
          convertedValue={
            pendingVnoUsd
              ? formatTokenAmount(pendingVnoUsd) + ' USD'
              : undefined
          }
          convertedValueLoading={pendingVnoUsdLoading}
        />
        <Button
          hidden={isHarvestHidden}
          variant="primary"
          size="sm"
          alignSelf="flex-end"
          _dark={{
            bg: 'brand-pink',
          }}
          onClick={(e) => {
            router.push(pathDict[coinMode].garden[gardenType].HARVEST);
            e.stopPropagation();
          }}
          mt="-8px"
        >
          {t('Harvest rewards')}
        </Button>
      </Flex>
    </Card>
  );
};

const useGardenInfoCard = (gardenType: GardenType) => {
  const { stakedFerro, stakedTectonic, stakedVvs, stakedVvsCro } =
    useLpAmountsStaked(gardenType);
  const { vvsGardenApy, ferroGardenApy, tectonicGardenApy, vvsCroGardenApy } =
    useGardenApy(gardenType);

  let staked = stakedFerro;
  let apy = ferroGardenApy;
  switch (gardenType) {
    case 'TECTONIC':
      staked = stakedTectonic;
      apy = tectonicGardenApy;
      break;
    case 'VVS':
      staked = stakedVvs;
      apy = vvsGardenApy;
      break;
    case 'VVS_CRO':
      staked = stakedVvsCro;
      apy = vvsCroGardenApy;
      break;
  }

  return {
    staked,
    apy: formatGardenApy(apy),
  };
};
